import React from "react";
import { Stack, Box, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useResponsive from "../../hooks/useResponsive";
import { useLocation, useNavigate } from "react-router-dom";
import Header from "../../layout/dashboard/header";
import { useNotificationService } from "../../components/notification";
import { useAuth } from "../../hooks/useAuth";
import { useMutation } from "@tanstack/react-query";
import {
  apiGetFacilitiesJobs,
  apiGetFacilitiesJobsAnalystics,
} from "../../api/facility";
import { getUrlPathId, getUrlPathTextId } from "../../utils/url-id";
import {
  FacilityJobsProps,
  FacilityJobStatsProps,
} from "../../interface/facility";
import {
  EmploymentDetailsModal,
  JobDetailsModal,
} from "../../components/hospital/modal-contents";
import EmployeeFacility from "../../components/hospital/employeeFacility";
import {
  PaginationDetails,
  PaginationDetailsDummyData,
} from "../../interface/api_request";
import JobFacility from "../../components/hospital/jobFacility";
import BackButton from "../../components/UIElements/BackButton";

const HospitalJobs = () => {
  const theme = useTheme();
  const isMedium = useResponsive("down", "md", "xl");
  const isSmall = useResponsive("down", "sm", "xl");
  const { showNotification } = useNotificationService();
  const { token } = useAuth();
  const location = useLocation();
  const [jobs_list, setJobs_list] = React.useState<FacilityJobsProps[]>([]);
  // set up pagination state
  const [pagination_state, setPagination_state] =
    React.useState<PaginationDetails>(PaginationDetailsDummyData);
  //
  const [filter_data, setFilter_data] = React.useState("");

  const [stats, setStats] = React.useState<FacilityJobStatsProps>({
    avgApplicants: 0,
    employmentTypeCount: {},
    jobTypeCount: {},
    statusCount: {},
    totalApplicants: 0,
    totalJobs: 0,
    totalPendingReviews: 0,
    totalShortlisted: 0,
    totalUpcomingInterviews: 0,
    workLocationCount: {},
    workTypeCount: {},
  });

  React.useEffect(() => {
    const fetchData = async () => {
      try {
        const facilityId = getUrlPathTextId(location?.pathname);
        const fields = { facilityId: facilityId, token: token };

        const values = {
          facilityId: facilityId,
          token: token,
          page: `${pagination_state?.page}`,
          filter: filter_data
        };
        // First call stats
        await statsRequest.mutateAsync(fields);
        // Then call jobs
        await mutation.mutateAsync(values);
      } catch (error) {
        console.error("Error in sequential calls:", error);
      }
    };
    fetchData();
  }, [pagination_state.page, filter_data]);

  const mutation = useMutation({
    mutationFn: (fields: { facilityId: string; token: string, page: string, filter: string }) =>
      apiGetFacilitiesJobs(fields),
    onSuccess: (data) => {
      if (data?.status === "error") {
        if ("msg" in data) {
          showNotification(data?.msg ?? "", "error");
          return;
        }
        return;
      }
      if ("data" in data) {
        setJobs_list(data?.data?.data);
        if (!!data?.data?.meta) {
          setPagination_state({
            hasNextPage: data?.data?.meta?.hasNextPage,
            hasPrevPage: data?.data?.meta?.hasPrevPage,
            nextPage: data?.data?.meta?.nextPage,
            page: data?.data?.meta?.page,
            pages: data?.data?.meta?.pages,
            prevPage: data?.data?.meta?.prevPage,
            reads: data?.data?.meta?.reads,
            totalDocs: data?.data?.meta?.totalDocs,
            unReads: data?.data?.meta?.unReads,
          });
        }
      }
    },
    onError: (error) => {
      showNotification("Fetch process failed", "error");
    },
  });
  const statsRequest = useMutation({
    mutationFn: (fields: { facilityId: string; token: string }) =>
      apiGetFacilitiesJobsAnalystics(fields),
    onSuccess: (data) => {
      
      if (data?.status === "error") {
        if ("msg" in data) {
          showNotification(data?.msg ?? "", "error");
          return;
        }
        return;
      }
      if ("data" in data) {
        // Handle stats data here
        setStats(data.data?.data);
        
      }
    },
    onError: (error) => {
      showNotification("Stats fetch failed", "error");
    },
  });


  return (
    <>
      {/* Header Menu */}
      <Header>
        <Box
          sx={{
            flexGrow: 1,
            display: "flex",
            flexDirection: "row",
            gap: "10px",
            justifyContent: "space-between",
            alignItems: "center",
            marginRight: "1rem",
            height: isMedium ? 60 : 75,
          }}
        >
          <Stack direction={"row"} alignItems={"center"} sx={{ gap: "10px" }}>
          <BackButton />
            <Typography
              variant={"subtitle1"}
              sx={{
                fontWeight: 500,
                fontFamily: "Lexend",
                color: theme.palette.secondary.dark,
              }}
            >
              Hospital Jobs
            </Typography>
          </Stack>
        </Box>
      </Header>
      
      
      <JobFacility
        jobs_list={jobs_list}
        loading={mutation.isPending}
        stats={stats}
        setFilterData={setFilter_data}
        pagination_state={pagination_state}
        setPagination_state={setPagination_state}
      />
    </>
  );
};

export default HospitalJobs;
